/**
 * Moldura do app: barra lateral recolhível (telas largas) ou barra inferior (celular), cabeçalho com título da página,
 * busca (paleta de comandos, Ctrl+K), sino e menu da pessoa; rodapé compacto com as frases de compliance.
 */
import { COMPLIANCE } from '@licita/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { cnpjFmt, editaisTodosQuery, iniciais } from '../lib/app';
import { contratosQuery } from '../lib/contratos';
import { PageMetaContext, usePreferencia, type PageMeta } from '../lib/ui';
import { SessionContext } from '../router';
import {
  IconBoard,
  IconBuilding,
  IconChevronDown,
  IconContract,
  IconList,
  IconLogout,
  IconMoon,
  IconSearch,
  IconSidebar,
  IconSun,
} from './icons';
import { Sino } from './notificacoes';
import { Paleta } from './paleta';
import { InstalarDica } from './push';
import { useTheme, Wordmark, Popover } from './ui';

const CONTATO = 'contato@licita.dev';

type Secao = 'quadro' | 'lista' | 'contratos' | 'empresa';
const NAV: { id: Secao; label: string; icon: ReactNode; to: '/' | '/contratos' | '/empresa'; search?: { vista: 'lista' } }[] = [
  { id: 'quadro', label: 'Quadro', icon: <IconBoard size={16} />, to: '/' },
  { id: 'lista', label: 'Lista', icon: <IconList size={16} />, to: '/', search: { vista: 'lista' } },
  { id: 'contratos', label: 'Contratos', icon: <IconContract size={16} />, to: '/contratos' },
  { id: 'empresa', label: 'Empresa', icon: <IconBuilding size={16} />, to: '/empresa' },
];

/** Seção ativa a partir da URL (o painel do edital fica "dentro" do quadro ou da lista). */
function secaoAtiva(pathname: string, search: Record<string, unknown>): Secao {
  if (pathname.startsWith('/contratos')) return 'contratos';
  if (pathname.startsWith('/empresa')) return 'empresa';
  return search.vista === 'lista' ? 'lista' : 'quadro';
}

const TITULOS: Record<Secao, string> = {
  quadro: 'Quadro de editais',
  lista: 'Lista de editais',
  contratos: 'Contratos',
  empresa: 'Empresa',
};

export default function Shell() {
  const [meta, setMetaState] = useState<PageMeta | null>(null);
  const setMeta = useCallback((m: PageMeta | null) => setMetaState(m), []);
  const ctx = useMemo(() => ({ meta, setMeta }), [meta, setMeta]);
  const [recolhida, setRecolhida] = usePreferencia('licita.sidebar', false);
  const [paleta, setPaleta] = useState(false);
  const { pathname, search } = useRouterState({ select: (s) => ({ pathname: s.location.pathname, search: s.location.search }) });
  const secao = secaoAtiva(pathname, search as Record<string, unknown>);
  const titulo = meta?.title ?? TITULOS[secao];
  const largo = pathname === '/' || pathname.startsWith('/editais/');

  // Ctrl/Cmd+K abre a paleta de qualquer lugar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaleta(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <PageMetaContext.Provider value={ctx}>
      <div className="flex min-h-dvh">
        <Sidebar secao={secao} recolhida={recolhida} onRecolher={() => setRecolhida(!recolhida)} />
        <div className="flex min-w-0 flex-1 flex-col">
          <HeaderMobile />
          <HeaderDesktop titulo={titulo} subtitulo={meta?.subtitle} abrirPaleta={() => setPaleta(true)} />
          <main id="conteudo" className={`w-full flex-1 px-4 pt-4 pb-6 lg:px-6 ${largo ? '' : 'mx-auto max-w-[1100px]'}`}>
            <Outlet />
          </main>
          <Footer comBarra />
        </div>
        <BarraInferior secao={secao} />
      </div>
      {paleta && <Paleta onFechar={() => setPaleta(false)} />}
    </PageMetaContext.Provider>
  );
}

// ---------- barra lateral ----------

function Sidebar({ secao, recolhida, onRecolher }: { secao: Secao; recolhida: boolean; onRecolher: () => void }) {
  const editais = useQuery(editaisTodosQuery);
  const contratos = useQuery(contratosQuery);
  const contagem: Partial<Record<Secao, number>> = {
    quadro: editais.data?.itens.length,
    contratos: contratos.data?.itens.length,
  };
  return (
    <aside
      className={`sticky top-0 hidden h-dvh shrink-0 flex-col gap-1.5 border-r border-line bg-bg-nav px-3 py-4 lg:flex ${recolhida ? 'w-[64px]' : 'w-[220px]'}`}
      aria-label="Seções"
    >
      <div className={`flex items-center pb-3 ${recolhida ? 'justify-center' : 'justify-between px-1.5'}`}>
        {!recolhida && (
          <Link to="/" className="rounded-md" aria-label="licita.dev, início">
            <Wordmark size={15} />
          </Link>
        )}
        <button
          type="button"
          className="app-btn app-btn-ghost size-7 min-h-0 shrink-0 bg-transparent px-0 text-mute"
          aria-label={recolhida ? 'Expandir menu' : 'Recolher menu'}
          aria-expanded={!recolhida}
          title={recolhida ? 'Expandir menu' : 'Recolher menu'}
          onClick={onRecolher}
        >
          <IconSidebar size={14} />
        </button>
      </div>
      <EmpresaBloco compacta={recolhida} />
      <nav aria-label="Principal" className="flex flex-col gap-1">
        {NAV.map((n) => (
          <Link
            key={n.id}
            to={n.to}
            search={n.search}
            className={`app-nav-link ${recolhida ? 'justify-center px-0' : ''}`}
            aria-current={secao === n.id ? 'page' : undefined}
            title={recolhida ? n.label : undefined}
          >
            {n.icon}
            <span className={recolhida ? 'sr-only' : ''}>{n.label}</span>
            {!recolhida && contagem[n.id] != null && <span className="ml-auto font-mono text-[11px] text-mute">{contagem[n.id]}</span>}
          </Link>
        ))}
      </nav>
      {!recolhida && (
        <div className="mt-auto flex flex-col gap-2">
          <InstalarDica compacta className="rounded-[10px] border border-line bg-bg-2 px-2.5 py-3 text-[12px] leading-relaxed text-mute" />
          <div className="rounded-[10px] border border-dashed border-line px-2.5 py-3 text-[12px] leading-relaxed text-mute">
            <span className="mb-0.5 block font-semibold text-fg">Equipe licita.dev</span>
            Responde em dias úteis. Use a conversa do edital ou{' '}
            <a href={`mailto:${CONTATO}`} className="text-accent hover:underline">
              {CONTATO}
            </a>
            .
          </div>
        </div>
      )}
    </aside>
  );
}

/**
 * Empresa ativa: nome + CNPJ. Com mais de uma empresa, um <select> invisível cobre o bloco (teclado e leitor de tela
 * veem um select comum); trocar zera o cache e mantém a rota.
 */
export function EmpresaBloco({ compacta = false, className = '' }: { compacta?: boolean; className?: string }) {
  const { empresas, empresaId, trocarEmpresa } = useContext(SessionContext);
  const ativa = empresas.find((e) => e.id === empresaId) ?? empresas[0];
  if (!ativa) return null;
  const varias = empresas.length > 1;
  const conteudo = (
    <>
      <span
        aria-hidden="true"
        className="flex size-7 shrink-0 items-center justify-center rounded-lg text-[12px] font-bold text-accent"
        style={{ background: 'var(--accent-soft)' }}
      >
        {iniciais(ativa.nome)}
      </span>
      {!compacta && (
        <span className="flex min-w-0 flex-col text-left">
          <span className="truncate text-[13px] font-semibold">{ativa.nome}</span>
          <span className="font-mono text-[11px] text-mute">{cnpjFmt(ativa.cnpj)}</span>
        </span>
      )}
      {varias && !compacta && (
        <span className="ml-auto shrink-0 text-mute" aria-hidden="true">
          <IconChevronDown size={14} />
        </span>
      )}
    </>
  );
  const classe = `flex w-full items-center gap-2.5 rounded-[10px] border border-line bg-bg-2 ${compacta ? 'justify-center p-1.5' : 'px-2.5 py-2'}`;
  if (!varias) {
    return (
      <div className={`mb-2 ${classe} ${className}`} title={compacta ? `${ativa.nome} · ${cnpjFmt(ativa.cnpj)}` : undefined}>
        {conteudo}
      </div>
    );
  }
  // Mais de uma empresa: menu próprio (o select nativo abria branco no tema escuro).
  return (
    <Popover
      rotulo="Empresa ativa"
      largura={compacta ? 260 : 232}
      className={`mb-2 ${className}`}
      botao={({ abrir, props }) => (
        <button
          type="button"
          {...props}
          onClick={abrir}
          className={`${classe} cursor-pointer text-fg hover:border-accent`}
          title={compacta ? `${ativa.nome} · ${cnpjFmt(ativa.cnpj)}` : 'Trocar de empresa'}
        >
          {conteudo}
        </button>
      )}
    >
      {(fechar) => (
        <ul className="flex flex-col gap-0.5" aria-label="Empresas">
          {empresas.map((e) => {
            const atual = e.id === ativa.id;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  aria-pressed={atual}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-bg-3 ${atual ? 'bg-bg-3' : ''}`}
                  onClick={() => {
                    fechar();
                    if (!atual) trocarEmpresa(e.id);
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="flex size-7 shrink-0 items-center justify-center rounded-lg text-[12px] font-bold text-accent"
                    style={{ background: 'var(--accent-soft)' }}
                  >
                    {iniciais(e.nome)}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[13px] font-semibold">{e.nome}</span>
                    <span className="font-mono text-[11px] text-mute">{cnpjFmt(e.cnpj)}</span>
                  </span>
                  {atual && <span className="ml-auto text-[11px] text-mute">ativa</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Popover>
  );
}

// ---------- cabeçalhos ----------

function HeaderDesktop({ titulo, subtitulo, abrirPaleta }: { titulo: string; subtitulo?: string; abrirPaleta: () => void }) {
  return (
    <header
      className="sticky top-0 z-20 hidden flex-wrap items-center gap-3 border-b border-line px-6 py-2.5 backdrop-blur-md lg:flex"
      style={{ background: 'var(--bg-header)' }}
    >
      <h1 className="text-[16px] font-semibold tracking-[-0.2px]">{titulo}</h1>
      {subtitulo && <span className="text-[12px] text-mute">{subtitulo}</span>}
      <button
        type="button"
        onClick={abrirPaleta}
        className="ml-auto flex h-9 min-w-[260px] max-w-[420px] flex-1 items-center gap-2 rounded-[9px] border border-line bg-bg-2 px-2.5 text-left text-[13px] text-mute hover:text-fg"
        aria-label="Buscar edital, órgão ou ação (Ctrl+K)"
        aria-keyshortcuts="Control+K"
      >
        <IconSearch size={14} />
        <span className="flex-1 truncate">Buscar edital, órgão ou ação…</span>
        <kbd className="rounded-[5px] border border-line px-1.5 py-0.5 font-mono text-[10px] text-mute">Ctrl K</kbd>
      </button>
      <Sino />
      <MenuPessoa />
    </header>
  );
}

function HeaderMobile() {
  return (
    <header className="flex items-center gap-2 border-b border-line bg-bg-nav px-4 py-2.5 lg:hidden">
      <Link to="/" aria-label="licita.dev, início">
        <Wordmark size={14} />
      </Link>
      <EmpresaBloco className="mb-0! ml-auto max-w-[46vw] py-1! pr-2! pl-1.5!" />
      <Sino />
      <MenuPessoa />
    </header>
  );
}

/** Iniciais da pessoa → menu com e-mail, tema e sair. */
function MenuPessoa() {
  const { me, signOut } = useContext(SessionContext);
  const [theme, toggleTheme] = useTheme();
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const nome = me?.user.name || me?.user.email || '';

  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAberto(false);
        btn.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [aberto]);

  return (
    <div ref={raiz} className="relative shrink-0">
      <button
        ref={btn}
        type="button"
        className="flex size-9 items-center justify-center rounded-[9px] text-[12px] font-bold text-accent"
        style={{ background: 'var(--accent-soft)' }}
        aria-label={`Conta: ${nome}`}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((v) => !v)}
      >
        {iniciais(nome)}
      </button>
      {aberto && (
        <div id={id} role="menu" aria-label="Conta" className="app-pop absolute top-[calc(100%+6px)] right-0 z-30 w-[240px] p-1.5">
          <div className="px-2.5 py-2 text-[12px]">
            <div className="truncate font-medium text-fg">{me?.user.name}</div>
            <div className="truncate text-mute">{me?.user.email}</div>
          </div>
          <button type="button" role="menuitem" className="app-nav-link w-full text-fg" onClick={toggleTheme}>
            {theme === 'dark' ? <IconSun size={15} /> : <IconMoon size={15} />}
            {theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
          </button>
          <button type="button" role="menuitem" className="app-nav-link w-full text-fg" onClick={signOut}>
            <IconLogout size={15} /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- barra inferior (celular) ----------

function BarraInferior({ secao }: { secao: Secao }) {
  const navigate = useNavigate();
  return (
    <nav
      aria-label="Seções"
      className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t border-line bg-bg-nav px-2 pt-1.5 pb-[max(10px,env(safe-area-inset-bottom))] lg:hidden"
    >
      {NAV.map((n) => {
        const on = secao === n.id;
        return (
          <button
            key={n.id}
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => void navigate({ to: n.to, search: n.search ?? {} })}
            className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg text-[11px] ${on ? 'font-semibold text-accent' : 'text-mute'}`}
          >
            {n.icon}
            {n.label}
          </button>
        );
      })}
    </nav>
  );
}

// ---------- rodapé ----------

/** Rodapé compacto com as frases de compliance (fonte única: @licita/contracts). */
export function Footer({ comBarra = false }: { comBarra?: boolean }) {
  const { staging } = useContext(SessionContext);
  return (
    <footer className="border-t border-line">
      <div
        className={`flex w-full flex-col gap-1 px-4 py-4 text-[11px] leading-relaxed text-mute lg:px-6 ${comBarra ? 'pb-24 lg:pb-4' : ''}`}
      >
        <p>{COMPLIANCE.semGarantia}</p>
        <p>{COMPLIANCE.juridico}</p>
        <p>{COMPLIANCE.fee}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <a href="https://licita.dev/" className="text-accent hover:underline">
            licita.dev
          </a>
          {staging && <span className="app-chip">staging</span>}
        </p>
      </div>
    </footer>
  );
}
